/**
 * driveee CarLo — Frontend bundle entry.
 *
 * Plan 05-01 — bootstraps the global Interactivity store. Block-specific
 * stores (e.g. driveeeCarlo for the archive) are registered on a per-block
 * basis from assets/blocks/<name>/view.ts.
 *
 * Lightbox (PhotoSwipe) is lazy-loaded on first .pswp-gallery click via
 * dynamic import (Pitfall 4 cure from RESEARCH.md).
 */
/**
 * v6.0 Phase 02 / FRONT-01 — this import is what makes the stylesheet exist.
 *
 * Vite emits CSS only for files reachable from a JS entry (or listed as an entry
 * themselves). styles/main.css was in neither, so dist/frontend/main.css was
 * never produced and the `is_file()` guard in AssetsEnqueuer.php:73 silently
 * skipped the <link> — 519 lines of finished CSS that never reached a browser.
 * Before this line, the only reference to the file anywhere in the repo was a
 * comment inside the file itself.
 */
import './styles/main.css';

import { store } from '@wordpress/interactivity';

/**
 * Tauscht den sichtbaren Betrag im Preiskasten gegen die andere Fassung.
 *
 * Beide Zeichenketten liefert der Server als Datenattribute mit
 * (`PriceBoxRenderer::priceDataAttrs`) — der Browser rechnet nichts, formatiert
 * nichts und kennt keine Mehrwertsteuer. Fehlt eine der beiden, passiert
 * nichts: dann gibt es zu diesem Fahrzeug schlicht keinen zweiten Preis
 * (differenzbesteuert).
 */
function applyPriceMode(box: HTMLElement, mode: 'gross' | 'net'): void {
  const gross = box.dataset.carloPriceGross;
  const net = box.dataset.carloPriceNet;
  if (!gross || !net) return;

  const amount = box.querySelector<HTMLElement>('.carlo-pricebox__amount');
  if (!amount) return;

  amount.textContent = mode === 'net' ? net : gross;
  box.dataset.carloPriceMode = mode;

  const toggle = box.querySelector<HTMLElement>('.carlo-pricebox__toggle');
  toggle?.setAttribute('aria-pressed', mode === 'net' ? 'true' : 'false');
}

/**
 * Gemerkte Vorliebe beim Laden anwenden — auf jeden Preiskasten der Seite,
 * also auch auf die Karten im Archiv.
 */
function applyPricePreference(): void {
  let pref: string | null = null;
  try {
    pref = window.localStorage.getItem('carlo_price_display_pref');
  } catch {
    pref = null;
  }
  if (pref !== 'net' && pref !== 'gross') return;

  document
    .querySelectorAll<HTMLElement>('[data-carlo-pricebox]')
    .forEach((box) => applyPriceMode(box, pref as 'gross' | 'net'));
}

interface CarloFrontendState {
  restUrl?: string;
  nonce?: string;
}

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    carloFrontend?: CarloFrontendState;
  }
}

// Register the global namespace so other view modules can extend it.
store('driveeeCarlo', {
  state: {},
  actions: {
    /**
     * togglePriceDisplay — schaltet den Preiskasten zwischen brutto und netto.
     *
     * 09.08.2026, Befund B6. Die einzige Fassung dieser Aktion stand in
     * `assets/blocks/archive/view.ts` und tat genau eines: sie schrieb
     * `localStorage.carlo_price_display_pref`. Das sichtbare Markup rührte sie
     * nicht an — am laufenden System gemessen stand vor und nach dem Klick
     * dasselbe „19.580 € brutto". Zwei Gründe, warum das niemandem auffiel:
     * der Block-Bundle wird auf der Detailseite gar nicht geladen (im
     * ausgelieferten ZIP sogar nirgends), und die Direktive lag ausserhalb
     * jeder Interactivity-Region.
     *
     * Jetzt lebt sie hier, im immer geladenen Hauptbundle, und tauscht den
     * Text gegen die zweite Fassung, die `PriceBoxRenderer` als
     * `data-carlo-price-gross` / `data-carlo-price-net` mitliefert. Die
     * Vorliebe wird weiterhin gemerkt und beim Laden angewandt
     * (`applyPricePreference`).
     */
    togglePriceDisplay(event: Event): void {
      const button = event.currentTarget as HTMLElement | null;
      const box = button?.closest('[data-carlo-pricebox]') as HTMLElement | null;
      if (!box) return;

      const next = box.dataset.carloPriceMode === 'net' ? 'gross' : 'net';
      applyPriceMode(box, next);

      try {
        window.localStorage.setItem('carlo_price_display_pref', next);
      } catch {
        // localStorage nicht verfügbar — die Umschaltung gilt trotzdem für
        // diese Seite, sie wird nur nicht gemerkt.
      }
    },

    /**
     * submitSort — schickt das Sortier-Formular beim Umschalten ab.
     *
     * 09.08.2026, Befund B18. Das Archiv hatte kein Sortier-Bedienelement;
     * serverseitig sortieren konnte es die ganze Zeit. Das neue Element ist
     * ein gewöhnliches `<form method="get">` mit Absende-Knopf — damit
     * funktioniert es auch ohne JavaScript, und genau das ist der Punkt.
     *
     * Diese Aktion ist die Bequemlichkeitsschicht darüber: wer JavaScript hat,
     * soll nach dem Umschalten nicht noch einen Knopf drücken müssen. Sie
     * NAVIGIERT (`form.submit()`) statt den Store zu ändern — dieselbe Lehre
     * wie bei den Chips: eine Store-Aktion holt einen neuen Zählerstand,
     * ordnet aber das serverseitig gerenderte Raster nicht um. Sortieren, das
     * die Reihenfolge nicht ändert, wäre schlimmer als keine Sortierung.
     *
     * Der Absende-Knopf bleibt dabei SICHTBAR, statt sich per Klasse aus dem
     * Weg zu blenden. In diesem Repo ist dreimal ein Bedienweg still
     * verschwunden, weil eine Direktive nicht griff — das Lead-Formular lag
     * ausserhalb jeder Interactivity-Region, das Modul wurde nirgends geladen,
     * der Block-Bundle fehlte im ZIP. Ein Knopf, den man nicht braucht, ist
     * harmlos; ein Weg, den es nur mit funktionierendem JavaScript gibt, ist
     * der Fehler, den es hier schon gab.
     *
     * Ereignis-Direktiven bekommen das Event DIREKT, kein `{ event }`-Objekt.
     */
    submitSort(event: Event): void {
      const select = event.currentTarget as HTMLSelectElement | null;
      const form = select?.closest('form') as HTMLFormElement | null;
      if (!form) return;

      form.submit();
    },

    /**
     * recordConversion — Plan 05-04 DETL-08 client-side wiring.
     *
     * Bound on Anrufen + WhatsApp buttons (CtaBarRenderer:
     * data-wp-on--click="actions.recordConversion"). Two side effects:
     *
     *   1. Push DataLayer event so GTM / GA4 picks it up.
     *   2. Fire-and-forget POST /carlo/v1/ab-events to persist the row.
     */
    recordConversion(event: Event): void {
      const target = event.currentTarget as HTMLElement | null;
      if (!target) return;

      const conversionType = target.dataset.carloConversionType ?? 'unknown';
      const vehicleScope = target.closest('[data-vehicle-id]') as HTMLElement | null;
      const vehicleId = Number(vehicleScope?.dataset.vehicleId ?? '0');
      const cookieMatch = document.cookie.match(/carlo_ab_bucket=([^;]+)/);
      const bucket = cookieMatch ? cookieMatch[1] : 'unknown';

      // 1. DataLayer push.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'carlo_conversion',
        conversion_type: conversionType,
        vehicle_id: vehicleId,
        bucket,
      });

      // 2. REST persist (fire-and-forget; UI doesn't block on it).
      const cfg = window.carloFrontend ?? {};
      if (cfg.restUrl && cfg.nonce && conversionType !== 'unknown') {
        // Map vehicles → ab-events (the restUrl is rooted at /carlo/v1/vehicles).
        const url = cfg.restUrl.replace(/\/vehicles\/?$/, '/ab-events');
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': cfg.nonce,
          },
          body: JSON.stringify({
            event_type: conversionType,
            vehicle_id: vehicleId,
            bucket,
          }),
          keepalive: true,
        }).catch(() => {
          // Swallow — DataLayer was the primary signal; persistence is best-effort.
        });
      }
    },
  },
});

/**
 * carlo/cta-bar — Plan 02-04 (TMPL-02). The sticky CTA bar's Anfrage
 * always-on button opens an in-context bottom-sheet that REUSES the real
 * server-rendered lead-form partial. Sheet markup lives in
 * templates/partials/cta-bar.php (.carlo-lead-sheet, role=dialog).
 *
 * `openLeadSheet` is the SINGLE click handler on the Anfrage button
 * (data-wp-on--click="actions.openLeadSheet"). Two side effects:
 *   1. Fire the `lead` conversion via the EXISTING recordConversion seam
 *      (data-carlo-conversion-type="lead" — already a whitelisted
 *      EventRecorder type; zero new conversion infra).
 *   2. Open the bottom-sheet (CSS slide-up via .carlo-lead-sheet--open).
 *
 * Close paths: backdrop click, X button, swipe-down on the panel, Escape.
 * The lead-form submission seam (carlo/lead-form store → /carlo/v1/leads)
 * is byte-untouched — this store only governs sheet open/close.
 */
/**
 * 09.08.2026, Befund B6 — die Annahme im alten Kommentar stimmte nicht.
 *
 * Dort stand: „Anfrage button + sheet share the same data-vehicle-id scope
 * wrapper." Tun sie nicht. Der Knopf sitzt in `.carlo-cta-bar`, das Blatt ist
 * dessen GESCHWISTER in der Seitenspalte. `scope.querySelector()` lieferte
 * deshalb `null`, und `openLeadSheet` lief bis zum Ende durch, ohne irgendetwas
 * zu öffnen — die Aktion feuerte nachweislich (die Laufzeit warnte über
 * `event.currentTarget`), nur blieb sie wirkungslos.
 *
 * Jetzt drei Stufen: im eigenen Bereich suchen, sonst über die
 * Fahrzeug-Kennung im ganzen Dokument, sonst das einzige Blatt der Seite.
 */
function findLeadSheet(from: HTMLElement | null): HTMLElement | null {
  const scope = from?.closest('[data-vehicle-id]') as HTMLElement | null;

  const local = scope?.querySelector?.('.carlo-lead-sheet') as HTMLElement | null;
  if (local) return local;

  const vehicleId = scope?.getAttribute('data-vehicle-id');
  if (vehicleId) {
    const escaped =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(vehicleId)
        : vehicleId.replace(/["\\]/g, '\\$&');
    const byId = document.querySelector(
      `.carlo-lead-sheet[data-vehicle-id="${escaped}"]`
    ) as HTMLElement | null;
    if (byId) return byId;
  }

  return document.querySelector('.carlo-lead-sheet');
}

let sheetPointerStartY: number | null = null;

/**
 * Zugriff auf den reaktiven Zustand des cta-bar-Stores.
 *
 * Als Funktion und nicht als Konstante, weil `store()` erst weiter unten
 * aufgerufen wird — zum Zeitpunkt des Aktionsaufrufs steht der Proxy längst.
 */
function ctaBarState(): { leadSheetOpen: boolean } {
  return (store('carlo/cta-bar') as unknown as { state: { leadSheetOpen: boolean } }).state;
}

const ctaBarStore = {
  state: {
    leadSheetOpen: false,
  },
  actions: {
    openLeadSheet(event: Event): void {
      // 1. Delegate to the existing conversion seam (lead attribution).
      try {
        // `store()` ist als `{ state: object }` typisiert; die Aktionen des
        // Archiv-Stores (blocks/archive/view.ts) stehen dort nicht im Typ,
        // existieren zur Laufzeit aber. Der Aufruf ist ohnehin in try/catch.
        (
          store('driveeeCarlo') as unknown as {
            actions?: { recordConversion?: (e: Event) => void };
          }
        ).actions?.recordConversion?.(event);
      } catch (_e) {
        // recordConversion is best-effort; never block the sheet open.
      }
      // 2. Open the bottom-sheet.
      //
      // 09.08.2026 — über den REAKTIVEN Zustand, nicht über das Literal.
      // `ctaBarStore` ist das rohe Objekt; die Bindungen
      // (`data-wp-bind--hidden`, `data-wp-class--…`) hängen am Proxy, den
      // `store()` zurückgibt. Eine Zuweisung am Literal sah im Debugger
      // richtig aus und erreichte das DOM nie.
      ctaBarState().leadSheetOpen = true;
      const target = event.currentTarget as HTMLElement | null;
      const sheet = findLeadSheet(target);
      if (sheet) {
        sheet.removeAttribute('hidden');
        sheet.classList.add('carlo-lead-sheet--open');
        const onKey = (ev: KeyboardEvent): void => {
          if (ev.key === 'Escape') {
            ctaBarStore.actions.closeLeadSheet(ev as unknown as Event);
            document.removeEventListener('keydown', onKey);
          }
        };
        document.addEventListener('keydown', onKey);
      }
    },
    closeLeadSheet(event: Event): void {
      ctaBarState().leadSheetOpen = false;
      const target = event.currentTarget as HTMLElement | null;
      const sheet =
        (target?.closest('.carlo-lead-sheet') as HTMLElement | null) ??
        findLeadSheet(target);
      if (sheet) {
        sheet.classList.remove('carlo-lead-sheet--open');
        sheet.setAttribute('hidden', '');
      }
      sheetPointerStartY = null;
    },
    onSheetSwipe(event: Event): void {
      // Swipe-down-to-close: track pointerdown→pointerup on the panel.
      const pe = event as PointerEvent;
      if (pe.type === 'pointerdown') {
        sheetPointerStartY = pe.clientY;
        return;
      }
      if (sheetPointerStartY !== null && pe.clientY - sheetPointerStartY > 80) {
        ctaBarStore.actions.closeLeadSheet(event);
      }
      sheetPointerStartY = null;
    },
  },
};

store('carlo/cta-bar', ctaBarStore);

/**
 * carlo/showroom — Plan 02-07 (TMPL-10). The Showroom-Fullscreen template's
 * persistent "Fahrzeugdaten" handle opens a DRAGGABLE bottom-sheet specs
 * drawer (.carlo-showroom-sheet) over the 100svh hero. Markup lives in
 * templates/single-carlo_vehicle-showroom-fullscreen.php.
 *
 * Drag physics REUSE the proven Plan 02-04 sheet mechanic verbatim
 * (pointerdown→pointerup Y-delta > 80 px = collapse) for consistency with
 * the Anfrage sheet (Claude's-discretion per 02-CONTEXT). The drawer is the
 * primary content surface (not a transient modal): the handle is always
 * visible over the hero, open/close only toggles the slide-up class —
 * `position:fixed` off-canvas transform origin means the drawer is never
 * the LCP element and shifts nothing (CLS-safe by construction).
 */
function findShowroomSheet(from: HTMLElement | null): HTMLElement | null {
  const root = from?.closest('.carlo-template--showroom-fullscreen') ?? document.body;
  return (root as HTMLElement | null)?.querySelector?.('.carlo-showroom-sheet') ?? null;
}

let showroomPointerStartY: number | null = null;

const showroomStore = {
  state: {
    drawerOpen: false,
  },
  actions: {
    openDrawer(event: Event): void {
      showroomStore.state.drawerOpen = true;
      const target = event.currentTarget as HTMLElement | null;
      const sheet = findShowroomSheet(target);
      if (sheet) {
        sheet.classList.add('carlo-showroom-sheet--open');
        const onKey = (ev: KeyboardEvent): void => {
          if (ev.key === 'Escape') {
            showroomStore.actions.closeDrawer(ev as unknown as Event);
            document.removeEventListener('keydown', onKey);
          }
        };
        document.addEventListener('keydown', onKey);
      }
    },
    closeDrawer(event: Event): void {
      showroomStore.state.drawerOpen = false;
      const target = event.currentTarget as HTMLElement | null;
      const sheet =
        (target?.closest('.carlo-showroom-sheet') as HTMLElement | null) ??
        findShowroomSheet(target);
      if (sheet) {
        sheet.classList.remove('carlo-showroom-sheet--open');
      }
      showroomPointerStartY = null;
    },
    onSheetSwipe(event: Event): void {
      // Swipe-down-to-collapse — identical mechanic to the Plan 02-04 sheet.
      const pe = event as PointerEvent;
      if (pe.type === 'pointerdown') {
        showroomPointerStartY = pe.clientY;
        return;
      }
      if (showroomPointerStartY !== null && pe.clientY - showroomPointerStartY > 80) {
        showroomStore.actions.closeDrawer(event);
      }
      showroomPointerStartY = null;
    },
  },
};

store('carlo/showroom', showroomStore);

// Lazy PhotoSwipe boot — first click anywhere on a .pswp-gallery anchor.
// Delegates to the dedicated lightbox bundle (assets/frontend/lightbox.ts)
// via dynamic import so the PhotoSwipe payload is NOT loaded until the
// visitor actually opens the gallery. Plan 05-04 Pitfall 4 cure.
function lazyBootPhotoSwipe(): void {
  let booted = false;
  document.addEventListener(
    'click',
    async (event) => {
      if (booted) return;
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest?.('.pswp-gallery a') as HTMLAnchorElement | null;
      if (!anchor) return;

      booted = true;
      event.preventDefault();
      try {
        const { initLightbox } = await import('./lightbox');
        await initLightbox(anchor);
      } catch (_err) {
        // Lightbox bundle unavailable — fall back to default anchor navigation.
        window.location.href = anchor.href;
      }
    },
    { passive: false }
  );
}

// Lazy finance-calculator boot — Plan 02-05 TMPL-12. The calculator ships
// as a collapsed <details> below the price box. We import the calculator
// module (assets/frontend/finance-calculator.ts) ONLY on the FIRST expand
// of that accordion, so a buyer who never opens it pays 0 KB and the
// collapsed/lazy design keeps it off the above-the-fold LCP path.
//
// ZERO network: the imported module computes the rate purely client-side
// from the rendered price DOM — no fetch/XHR anywhere in this path.
function lazyBootFinanceCalculator(): void {
  let financeBooted = false;
  const details = document.querySelector(
    '[data-carlo-finance] details'
  ) as HTMLDetailsElement | null;
  if (!details) return;

  details.addEventListener('toggle', async () => {
    if (financeBooted || !details.open) return;
    financeBooted = true;
    try {
      const { initFinanceCalculator } = await import('./finance-calculator');
      initFinanceCalculator();
    } catch (_err) {
      // Calculator bundle unavailable — the <details> still renders its
      // static disclaimer + controls; only the live computation is absent.
      financeBooted = false;
    }
  });
}

// Galerie-Slider — v6.0 Phase 06 / Schritt 5, DESIGN-04 (UI-SPEC §6).
//
// Das Scrollen macht CSS (scroll-snap in critical.css); dieses Modul liefert
// nur Pfeile, Zähler, Miniaturen und Pfeiltasten. Es kommt per dynamischem
// Import, weil Bild 0 der LCP-Kandidat ist und kein Skript davorstehen darf.
// Ohne das Modul bleibt die Galerie wisch- und scrollbar (§6.3).
function lazyBootGallery(): void {
  if (!document.querySelector('[data-carlo-gallery]')) return;

  void import('./gallery')
    .then(({ initGallery }) => initGallery())
    .catch(() => {
      // Modul nicht verfügbar — Scroll-Snap trägt die Galerie weiter.
    });
}

// Filter-Facetten — v6.0 Phase 06 / Schritt 6, DESIGN-03 (UI-SPEC §4.4).
//
// Öffnen/Schließen macht `<details>` von sich aus; dieses Modul ergänzt nur
// Escape, Klick-daneben und das mobile Sheet. Ohne es bleibt die Filterung
// vollständig bedienbar — deshalb dynamisch und erst nach dem ersten Paint.
function lazyBootFacets(): void {
  if (!document.querySelector('.carlo-filter-form__form')) return;

  void import('./facets')
    .then(({ initFacets }) => initFacets())
    .catch(() => {
      // Modul nicht verfügbar — <details> und method="get" tragen das Formular.
    });
}

// Öffnungs-Status der Ansprechpartner-Karte — 08.08.2026.
//
// Der Satz „Jetzt geöffnet — bis 18:00 Uhr" kann serverseitig nicht
// entstehen, weil Detailseiten als HTML zwischengespeichert werden und ein
// gecachtes „geöffnet" um Mitternacht lügt. Ohne dieses Modul bleibt die
// Statuszeile leer; die vollständige Öffnungszeiten-Tabelle steht im
// `<details>` daneben und ist ohne JavaScript vollständig lesbar.
function lazyBootOpeningStatus(): void {
  if (!document.querySelector('[data-carlo-hours]')) return;

  void import('./opening-status')
    .then(({ initOpeningStatus }) => initOpeningStatus())
    .catch(() => {
      // Modul nicht verfügbar — die Tabelle im <details> trägt die Zeiten.
    });
}

// Parken & Vergleichen — Block D2, 13.08.2026.
//
// Dynamisch und erst nach dem ersten Paint, aus demselben Grund wie Galerie
// und Facetten: kein Skript vor dem LCP-Bild. Der Preis dafür ist, dass die
// Schalter für einen Wimpernschlag „Parken" zeigen, obwohl das Fahrzeug
// geparkt ist — das ist der neutrale Zustand, kein falscher.
//
// Der Wächter fragt BEIDE Anker ab: auf der Detailseite gibt es Schalter ohne
// Leiste, wenn der Betreiber `compare` abgewählt hat, und die Vergleichsseite
// selbst besteht aus Karten mit Schaltern.
function lazyBootParking(): void {
  if (!document.querySelector('[data-carlo-park],[data-carlo-parkbar]')) return;

  void import('./parking')
    .then(({ initParking }) => initParking())
    .catch(() => {
      // Modul nicht verfügbar — die Seite bleibt vollständig lesbar, nur das
      // Parken funktioniert nicht.
    });
}

// Exposé — Block D1, 13.08.2026.
//
// Der Druckknopf der Exposé-Seite (`/fahrzeuge/…/?expose=1`). Er steht
// serverseitig auf `disabled` und wird hier — und NUR hier — bedienbar:
// erscheinen darf er erst, wenn der Handler existiert, der ihn bedient. Ohne
// Bundle bleibt er unsichtbar, und der Hinweis daneben nennt den Weg über das
// Druckmenü des Browsers. Dieselbe Regel wie bei den CTA-Knöpfen: ein Knopf,
// der ins Leere führt, ist schlimmer als keiner.
//
// `disabled` und nicht `hidden`, weil ein Knopf, der keinen Platz belegt, beim
// Erscheinen das Blatt schiebt — gemessen 60 px, CLS 0,0621. Begründung mit
// Zahlen in expose.css.
//
// Kein automatisches window.print(): ein Druckdialog, der sich beim Laden
// selbst öffnet, blockiert die Seite für den Besucher — und für jeden
// automatisierten Lauf (axe, Lighthouse, Playwright) gleich mit.
function bootExposePrint(): void {
  const buttons = document.querySelectorAll<HTMLElement>('[data-carlo-expose-print]');
  if (buttons.length === 0) return;

  buttons.forEach((button) => {
    button.removeAttribute('disabled');
    button.addEventListener('click', () => window.print());
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', lazyBootOpeningStatus, {
    once: true,
  });
  document.addEventListener('DOMContentLoaded', lazyBootPhotoSwipe, { once: true });
  document.addEventListener('DOMContentLoaded', lazyBootFinanceCalculator, {
    once: true,
  });
  document.addEventListener('DOMContentLoaded', lazyBootGallery, { once: true });
  document.addEventListener('DOMContentLoaded', lazyBootFacets, { once: true });
  document.addEventListener('DOMContentLoaded', lazyBootParking, { once: true });
  document.addEventListener('DOMContentLoaded', bootExposePrint, { once: true });
  document.addEventListener('DOMContentLoaded', applyPricePreference, { once: true });
} else {
  lazyBootOpeningStatus();
  lazyBootPhotoSwipe();
  lazyBootFinanceCalculator();
  lazyBootGallery();
  lazyBootFacets();
  lazyBootParking();
  bootExposePrint();
  applyPricePreference();
}

console.log('[carlo] frontend bundle loaded');

export {};
